import {
	useQuery,
	useMutation,
	useQueryClient,
} from "@tanstack/react-query";
import type { UseQueryResult, UseMutationResult } from "@tanstack/react-query";
import {
	getPanelMemberStatus,
	acceptInvitation,
	declineInvitation,
	reinviteMember,
	revokeMember,
	unrevokeMember,
	sendReminder,
	resendMemberInvite,
	toggleScoring,
	saveOrSubmitScores,
	getMyScores,
	getMemberScores,
	getAverageScores,
	getComparisonData,
	triggerAISummary,
	getAISummary,
	createPanelClarification,
	updatePanelClarification,
	deletePanelClarification,
	getMyPanelClarifications,
	proceedToNextStep,
} from "../api/panel-scoring-api";
import type { PanelMemberWithProgress, EvaluationPanelMember } from "../types/panel.type";
import type { DeclineInvitationPayload, ToggleScoringPayload } from "../types/panel.type";
import type {
	MyScoresResponse,
	MemberScoresResponse,
	AverageScoresResponse,
	ComparisonDataResponse,
	PanelScoreSummary,
	SaveScoresPayload,
	SaveScoresResult,
	PanelClarification,
	CreatePanelClarificationPayload,
	UpdatePanelClarificationPayload,
} from "../types/scoring.type";

// --- Query key factory ---

export type ScoringMode = "initial" | "final";

const panelKeys = {
	all: (rfxId: number, evalId: number) =>
		["evaluation-panel", rfxId, evalId] as const,
	members: (rfxId: number, evalId: number, mode: ScoringMode = "initial") =>
		[...panelKeys.all(rfxId, evalId), "members", mode] as const,
	myScores: (rfxId: number, evalId: number) =>
		[...panelKeys.all(rfxId, evalId), "my-scores"] as const,
	memberScores: (rfxId: number, evalId: number, memberId: number, mode: ScoringMode = "initial") =>
		[...panelKeys.all(rfxId, evalId), "member-scores", memberId, mode] as const,
	averages: (rfxId: number, evalId: number, mode: ScoringMode = "initial") =>
		[...panelKeys.all(rfxId, evalId), "averages", mode] as const,
	comparison: (rfxId: number, evalId: number, criteriaId: number, inviteId: number, mode: ScoringMode = "initial") =>
		[...panelKeys.all(rfxId, evalId), "comparison", criteriaId, inviteId, mode] as const,
	summary: (rfxId: number, evalId: number, criteriaId: number) =>
		[...panelKeys.all(rfxId, evalId), "summary", criteriaId] as const,
	myClarifications: (rfxId: number, evalId: number) =>
		[...panelKeys.all(rfxId, evalId), "my-clarifications"] as const,
};

export { panelKeys };

// --- Panel member queries ---

export const usePanelMemberStatus = (
	rfxId: number | undefined,
	evalId: number | undefined,
	mode: ScoringMode = "initial"
): UseQueryResult<PanelMemberWithProgress[]> => {
	return useQuery({
		queryKey: panelKeys.members(rfxId!, evalId!, mode),
		queryFn: () => getPanelMemberStatus(rfxId!, evalId!, mode),
		enabled: !!rfxId && !!evalId,
	});
};

export const useAcceptInvitation = (
	rfxId: number,
	evalId: number
): UseMutationResult<EvaluationPanelMember, Error, number> => {
	const queryClient = useQueryClient();
	return useMutation({
		mutationFn: (memberId: number) =>
			acceptInvitation(rfxId, evalId, memberId),
		onSuccess: () => {
			queryClient.invalidateQueries({
				queryKey: panelKeys.members(rfxId, evalId),
			});
		},
	});
};

export const useDeclineInvitation = (
	rfxId: number,
	evalId: number
): UseMutationResult<EvaluationPanelMember, Error, { memberId: number; payload: DeclineInvitationPayload }> => {
	const queryClient = useQueryClient();
	return useMutation({
		mutationFn: ({ memberId, payload }) =>
			declineInvitation(rfxId, evalId, memberId, payload),
		onSuccess: () => {
			queryClient.invalidateQueries({
				queryKey: panelKeys.members(rfxId, evalId),
			});
		},
	});
};

export const useReinviteMember = (
	rfxId: number,
	evalId: number
): UseMutationResult<EvaluationPanelMember, Error, number> => {
	const queryClient = useQueryClient();
	return useMutation({
		mutationFn: (memberId: number) =>
			reinviteMember(rfxId, evalId, memberId),
		onSuccess: () => {
			queryClient.invalidateQueries({
				queryKey: panelKeys.members(rfxId, evalId),
			});
		},
	});
};

export const useRevokeMember = (
	rfxId: number,
	evalId: number
): UseMutationResult<EvaluationPanelMember, Error, number> => {
	const queryClient = useQueryClient();
	return useMutation({
		mutationFn: (memberId: number) =>
			revokeMember(rfxId, evalId, memberId),
		onSuccess: () => {
			queryClient.invalidateQueries({
				queryKey: panelKeys.members(rfxId, evalId),
			});
		},
	});
};

export const useUnrevokeMember = (
	rfxId: number,
	evalId: number
): UseMutationResult<EvaluationPanelMember, Error, number> => {
	const queryClient = useQueryClient();
	return useMutation({
		mutationFn: (memberId: number) =>
			unrevokeMember(rfxId, evalId, memberId),
		onSuccess: () => {
			queryClient.invalidateQueries({
				queryKey: panelKeys.members(rfxId, evalId),
			});
		},
	});
};

export const useSendReminder = (
	rfxId: number,
	evalId: number
): UseMutationResult<void, Error, number> => {
	return useMutation({
		mutationFn: (memberId: number) =>
			sendReminder(rfxId, evalId, memberId),
	});
};

export const useResendMemberInvite = (
	rfxId: number,
	evalId: number
): UseMutationResult<void, Error, number> => {
	const queryClient = useQueryClient();
	return useMutation({
		mutationFn: (memberId: number) =>
			resendMemberInvite(rfxId, evalId, memberId),
		onSuccess: () => {
			queryClient.invalidateQueries({
				queryKey: panelKeys.members(rfxId, evalId),
			});
		},
	});
};

export const useToggleScoring = (
	rfxId: number,
	evalId: number
): UseMutationResult<EvaluationPanelMember | null, Error, ToggleScoringPayload> => {
	const queryClient = useQueryClient();
	return useMutation({
		mutationFn: (payload: ToggleScoringPayload) =>
			toggleScoring(rfxId, evalId, payload),
		onSuccess: () => {
			queryClient.invalidateQueries({
				queryKey: panelKeys.members(rfxId, evalId),
			});
		},
	});
};

// --- Scoring queries ---

export const useMyScores = (
	rfxId: number | undefined,
	evalId: number | undefined
): UseQueryResult<MyScoresResponse> => {
	return useQuery({
		queryKey: panelKeys.myScores(rfxId!, evalId!),
		queryFn: () => getMyScores(rfxId!, evalId!),
		enabled: !!rfxId && !!evalId,
	});
};

export const useMemberScores = (
	rfxId: number | undefined,
	evalId: number | undefined,
	memberId: number | undefined,
	mode: ScoringMode = "initial"
): UseQueryResult<MemberScoresResponse> => {
	return useQuery({
		queryKey: panelKeys.memberScores(rfxId!, evalId!, memberId!, mode),
		queryFn: () => getMemberScores(rfxId!, evalId!, memberId!, mode),
		enabled: !!rfxId && !!evalId && !!memberId,
	});
};

export const useAverageScores = (
	rfxId: number | undefined,
	evalId: number | undefined,
	mode: ScoringMode = "initial"
): UseQueryResult<AverageScoresResponse> => {
	return useQuery({
		queryKey: panelKeys.averages(rfxId!, evalId!, mode),
		queryFn: () => getAverageScores(rfxId!, evalId!, mode),
		enabled: !!rfxId && !!evalId,
	});
};

export const useComparisonData = (
	rfxId: number | undefined,
	evalId: number | undefined,
	criteriaId: number | undefined,
	inviteId: number | undefined,
	mode: ScoringMode = "initial"
): UseQueryResult<ComparisonDataResponse> => {
	return useQuery({
		queryKey: panelKeys.comparison(rfxId!, evalId!, criteriaId!, inviteId!, mode),
		queryFn: () => getComparisonData(rfxId!, evalId!, criteriaId!, inviteId!, mode),
		enabled: !!rfxId && !!evalId && !!criteriaId && !!inviteId,
	});
};

// --- Save/submit scores ---

export const useSaveScores = (
	rfxId: number,
	evalId: number
): UseMutationResult<SaveScoresResult, Error, SaveScoresPayload> => {
	const queryClient = useQueryClient();
	return useMutation({
		mutationFn: (payload: SaveScoresPayload) =>
			saveOrSubmitScores(rfxId, evalId, payload),
		onSuccess: (_data, variables) => {
			queryClient.invalidateQueries({
				queryKey: panelKeys.myScores(rfxId, evalId),
			});
			if (variables.action === "submit") {
				queryClient.invalidateQueries({
					queryKey: panelKeys.members(rfxId, evalId),
				});
				queryClient.invalidateQueries({
					queryKey: panelKeys.averages(rfxId, evalId),
				});
			}
		},
	});
};

export const useSubmitScores = (
	rfxId: number,
	evalId: number
): UseMutationResult<SaveScoresResult, Error, SaveScoresPayload["scores"]> => {
	const queryClient = useQueryClient();
	return useMutation({
		mutationFn: (scores: SaveScoresPayload["scores"]) =>
			saveOrSubmitScores(rfxId, evalId, { action: "submit", scores }),
		onSuccess: () => {
			queryClient.invalidateQueries({
				queryKey: panelKeys.myScores(rfxId, evalId),
			});
			queryClient.invalidateQueries({
				queryKey: panelKeys.members(rfxId, evalId),
			});
			queryClient.invalidateQueries({
				queryKey: panelKeys.averages(rfxId, evalId),
			});
		},
	});
};

// --- AI summary with polling ---

export const useAISummary = (
	rfxId: number | undefined,
	evalId: number | undefined,
	criteriaId: number | undefined
): UseQueryResult<PanelScoreSummary | null> & {
	trigger: UseMutationResult<void, Error, void>;
} => {
	const queryClient = useQueryClient();

	const queryResult = useQuery({
		queryKey: panelKeys.summary(rfxId!, evalId!, criteriaId!),
		queryFn: () => getAISummary(rfxId!, evalId!, criteriaId!),
		enabled: !!rfxId && !!evalId && !!criteriaId,
		refetchInterval: (query) => {
			const data = query.state.data;
			if (data && (data.status === "pending" || data.status === "processing")) {
				return 5000;
			}
			return false;
		},
	});

	const trigger = useMutation({
		mutationFn: () => triggerAISummary(rfxId!, evalId!, criteriaId!),
		onSuccess: () => {
			queryClient.invalidateQueries({
				queryKey: panelKeys.summary(rfxId!, evalId!, criteriaId!),
			});
		},
	});

	return { ...queryResult, trigger };
};

// --- Clarifications ---

export const useMyClarifications = (
	rfxId: number | undefined,
	evalId: number | undefined
): UseQueryResult<PanelClarification[]> => {
	return useQuery({
		queryKey: panelKeys.myClarifications(rfxId!, evalId!),
		queryFn: () => getMyPanelClarifications(rfxId!, evalId!),
		enabled: !!rfxId && !!evalId,
	});
};

export const useCreateClarification = (
	rfxId: number,
	evalId: number
): UseMutationResult<PanelClarification, Error, CreatePanelClarificationPayload> => {
	const queryClient = useQueryClient();
	return useMutation({
		mutationFn: (payload: CreatePanelClarificationPayload) =>
			createPanelClarification(rfxId, evalId, payload),
		onSuccess: () => {
			queryClient.invalidateQueries({
				queryKey: panelKeys.myClarifications(rfxId, evalId),
			});
		},
	});
};

export const useUpdateClarification = (
	rfxId: number,
	evalId: number
): UseMutationResult<PanelClarification, Error, { clarificationId: number; payload: UpdatePanelClarificationPayload }> => {
	const queryClient = useQueryClient();
	return useMutation({
		mutationFn: ({ clarificationId, payload }) =>
			updatePanelClarification(rfxId, evalId, clarificationId, payload),
		onSuccess: () => {
			queryClient.invalidateQueries({
				queryKey: panelKeys.myClarifications(rfxId, evalId),
			});
		},
	});
};

export const useDeleteClarification = (
	rfxId: number,
	evalId: number
): UseMutationResult<void, Error, number> => {
	const queryClient = useQueryClient();
	return useMutation({
		mutationFn: (clarificationId: number) =>
			deletePanelClarification(rfxId, evalId, clarificationId),
		onSuccess: () => {
			queryClient.invalidateQueries({
				queryKey: panelKeys.myClarifications(rfxId, evalId),
			});
		},
	});
};

// --- Proceed to next step ---

export const useProceedToNextStep = (
	rfxId: number,
	evalId: number
): UseMutationResult<void, Error, void> => {
	return useMutation({
		mutationFn: () => proceedToNextStep(rfxId, evalId),
	});
};
